import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../services/api';
import '../styles/AnnonceCard.css';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const imageRef = useRef(null);

  const images = product.images && product.images.length > 0 ? product.images : [];
  const hasMultipleImages = images.length > 1;

  // Image URL builder - use thumbnails for cards (faster loading)
  const getImageUrl = (img) => `/uploads/thumbnails/${img}`;
  const getFullImageUrl = (img) => `/uploads/${img}`;

  // IntersectionObserver for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  // Preload next image on hover
  useEffect(() => {
    if (isHovered && hasMultipleImages) {
      const nextIdx = (currentImageIndex + 1) % images.length;
      const img = new Image();
      img.src = getImageUrl(images[nextIdx]);
    }
  }, [isHovered, currentImageIndex, hasMultipleImages, images]);

  // Desktop: cycle images based on mouse position zones
  const handleMouseMove = useCallback((e) => {
    if (!hasMultipleImages || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const segmentWidth = rect.width / images.length;
    const newIndex = Math.min(
      Math.floor(x / segmentWidth),
      images.length - 1
    );
    if (newIndex !== currentImageIndex) {
      setCurrentImageIndex(newIndex);
    }
  }, [hasMultipleImages, images.length, currentImageIndex]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setCurrentImageIndex(0);
  };

  // Mobile: swipe between images
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0 && currentImageIndex < images.length - 1) {
        setCurrentImageIndex(prev => prev + 1);
      } else if (diff < 0 && currentImageIndex > 0) {
        setCurrentImageIndex(prev => prev - 1);
      }
    }
    setTouchStartX(null);
  };

  // Navigate to detail
  const handleCardClick = () => {
    if (touchStartX !== null) return;
    navigate(`/product/${product.id}`);
  };

  // Toggle favorite
  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      if (isFavorite) {
        await productsAPI.removeFromFavorites(product.id);
      } else {
        await productsAPI.addToFavorites(product.id);
      }
      setIsFavorite(!isFavorite);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  // Detect vehicle category
  const isVehicle = product.parent_category_slug === 'voitures' || product.category_slug === 'voitures';

  // Price formatting - no fees for vehicles, standard fees for others
  const price = parseFloat(product.price);
  const totalPrice = isVehicle
    ? price.toFixed(2)
    : (price + 1.5 + price * 0.02).toFixed(2);
  const originalPrice = product.original_price ? parseFloat(product.original_price) : null;
  const hasDiscount = originalPrice && originalPrice > price;
  const discountPercent = hasDiscount ? Math.round((1 - price / originalPrice) * 100) : 0;

  // Condition labels & colors
  const conditionConfig = {
    'neuf_etiquette': { label: t('conditionShort.neuf_etiquette'), color: '#00C851' },
    'neuf_sans_etiquette': { label: t('conditionShort.neuf_sans_etiquette'), color: '#00C851' },
    'tres_bon': { label: t('conditionShort.tres_bon'), color: '#2196F3' },
    'bon': { label: t('conditionShort.bon'), color: '#FF9800' },
    'satisfaisant': { label: t('conditionShort.satisfaisant'), color: '#9E9E9E' }
  };

  const condition = conditionConfig[product.condition] || null;

  return (
    <div
      className="product-card"
      ref={cardRef}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image Container */}
      <div
        className="product-card__image-wrapper"
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >

        {/* Blur placeholder */}
        {!imageLoaded && images.length > 0 && (
          <div className="product-card__placeholder">
            <div className="product-card__placeholder-pulse" />
          </div>
        )}

        {/* Main image - only load when visible */}
        {isVisible && images.length > 0 ? (
          <img
            ref={imageRef}
            src={getImageUrl(images[currentImageIndex])}
            alt={product.title}
            className={`product-card__image ${imageLoaded ? 'loaded' : ''}`}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              // Fallback to original if thumbnail not available
              if (e.target.src.includes('/thumbnails/')) {
                e.target.src = getFullImageUrl(images[currentImageIndex]);
              } else {
                e.target.style.display = 'none';
              }
            }}
            draggable="false"
          />
        ) : images.length === 0 ? (
          <div className="product-card__no-image">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        ) : null}

        {/* Desktop hover segments (Marko style) */}
        {hasMultipleImages && isHovered && (
          <div className="product-card__image-segments">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`product-card__segment ${idx === currentImageIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}

        {/* Mobile dots */}
        {hasMultipleImages && (
          <div className="product-card__dots">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`product-card__dot ${idx === currentImageIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}

        {/* Photo count */}
        {images.length > 1 && (
          <div className="product-card__photo-count">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            {images.length}
          </div>
        )}

        {/* Favorite button */}
        <button
          className={`product-card__favorite ${isFavorite ? 'active' : ''}`}
          onClick={handleFavoriteClick}
          aria-label={t('product.addToFavorites')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24"
            fill={isFavorite ? '#FF4081' : 'none'}
            stroke={isFavorite ? '#FF4081' : '#fff'}
            strokeWidth="2"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>

        {/* Sold overlay */}
        {product.status === 'sold' && (
          <div className="product-card__sold">{t('product.soldOverlay')}</div>
        )}

        {/* Discount badge */}
        {hasDiscount && (
          <div className="product-card__discount">-{discountPercent}%</div>
        )}

        {/* Condition badge */}
        {condition && (
          <div
            className="product-card__condition"
            style={{
              backgroundColor: condition.color,
              top: hasDiscount ? '38px' : '8px'
            }}
          >
            {condition.label}
          </div>
        )}
      </div>

      {/* Card Info */}
      <div className="product-card__info">
        <div className="product-card__price-row">
          <span className="product-card__price">{totalPrice} CHF</span>
          {hasDiscount && (
            <span className="product-card__original-price">
              {originalPrice.toFixed(2)}
            </span>
          )}
        </div>
        <div className="product-card__seller-price">
          {t('product.sellerPrice')}: {price.toFixed(2)} CHF
        </div>

        <div className="product-card__meta">
          {isVehicle ? (
            <>
              {product.vehicle_make && (
                <span className="product-card__brand-tag">{product.vehicle_make}</span>
              )}
              {product.vehicle_year && (
                <span className="product-card__tag">{product.vehicle_year}</span>
              )}
              {product.vehicle_mileage != null && (
                <span className="product-card__tag">{Number(product.vehicle_mileage).toLocaleString()} km</span>
              )}
              {product.vehicle_fuel && (
                <span className="product-card__tag">{product.vehicle_fuel}</span>
              )}
            </>
          ) : (
            <>
              {product.brand && (
                <span className="product-card__brand-tag">{product.brand}</span>
              )}
              {product.size && (
                <span className="product-card__tag">{product.size}</span>
              )}
            </>
          )}
          {product.city && (
            <span className="product-card__location">{product.city.trim()}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductCard);
